import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import { wrapCreateBrowserRouterV7 } from '@sentry/react'
import { lazy } from 'react'

import { ServiceError } from 'entities/Sentry/ui/ServiceError'
import { AnalyticsRoutes, HubspotWorkflowsRoutes } from 'entities/ExportRouters'
import { type EntitiesStore } from 'entities/EntitiesExport'
import { ProviderEntities } from 'entities/EntitiesExport/context/entitiesContext'
import { ReCaptchaLayout } from 'entities/Auth'
import { MenuAIAutomationLayout } from 'widgets/Menu/ui/MenuAIAutomation'
import { MenuOutboundLayout } from 'widgets/Menu/ui/MenuOutbound'
import { PresentationModeLayout } from 'widgets/PresentationMode'
import { MenuAnalyticsLayout } from 'widgets/Menu/ui/MenuAnalytics'
import type { CallPopUpGlobalStore } from 'widgets/CallPopUp'
import { ProviderCallPopUpGlobal } from 'widgets/CallPopUp/context/callPopUpGlobalContext'
import { PowerDialerRouter, PowerDialerRoutes } from 'pages/powerDialer'
import { TriggersRouter, TriggersRoutes } from 'pages/triggers'
import { KeywordsRouter, KeywordsRoutes } from 'pages/keywords'
import { BroadcastsRouter, BroadcastsRoutes } from 'pages/broadcasts'
import { ConversationRoutes, ConversationsRouter } from 'pages/conversations'
import { WorkflowsRouter, WorkflowsRoutes } from 'pages/workflows'
import { ChatbotRouter, ChatbotRoutes } from 'pages/chatbot'
import { ContactsRouter, ContactsRoutes } from 'pages/contacts'
import { AdminRoutes } from 'pages/admin'
import { SettingsRoutes } from 'pages/settings/route/type'
import { SettingsRouter } from 'pages/settings/route/SettingsRouter'

import { CampaignRouter, CampaignRoutes } from 'pages/campaigns'
import { ClientsRouter, ClientsRoutes } from 'pages/clients'
import { KnowledgeBaseRouter, KnowledgeBaseRoutes } from 'pages/settings/pages/knowledgeBase'
import { TextEnableLandlineRoutes } from 'pages/settings/pages/numbers/pages/TextEnableLandline'
import { NumbersRoutes } from 'pages/settings/pages/numbers/route/type'
import { HubspotWorkflowsRouter } from 'pages/hubspotWorkflows'
import { AnalyticsRouter } from 'pages/analytics'
import { DiscoverRoutes } from 'pages/discover/route/type'
import DiscoverRouter from 'pages/discover/route/discoverRouter'
import { AuthRouter, AuthRoutes } from 'pages/auth'
import { RegisterRouter, RegisterRoutes } from 'pages/register'
import PageNotFound from 'pages/pageNotFound'
import { InboxesRoutes } from 'pages/settings/pages/inboxes'
import { ClickToTextRouter, ClickToTextRoutes } from 'pages/clickToText'
import { SurveysRouter, SurveysRoutes } from 'pages/surveys'
import { ReviewsRouter, ReviewsRoutes } from 'pages/reviews'
import { SuspendedRouter } from 'pages/suspended'
import { SuspendedRoutes } from 'pages/suspended/route/type'
import { LandingMobileRouter, LandingMobileRoutes } from 'pages/landingMobile'
import {
  AccessOrganizationLayout,
  AuthLayoutVue,
  AuthRefreshLayout,
  PageNotFoundVueLayout,
  RootLayout,
  SubscriptionAccessLayout,
} from 'app/shared/layouts'
import { wrapInSuspense } from 'app/shared/lib/wrapInSuspense'
import { StatsigAppProvider } from 'app/shared/StatsigAppProvider'

const AdminRouter = lazy(() => import('pages/admin/route/adminRouter'))
const WorkflowsChildRouter = lazy(() => import('pages/workflows/route/workflowsChildRouter'))
const SmartCallFlowEdit = lazy(
  () => import('pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/edit')
)

const routes = createRoutesFromElements(
  <Route element={<PresentationModeLayout />}>
    <Route element={<RootLayout showAiAgent />} errorElement={<ServiceError name='next' />}>
      <Route element={<AuthRefreshLayout />}>
        <Route element={<AuthLayoutVue type='default' />}>
          <Route element={<SubscriptionAccessLayout />}>
            <Route element={<AccessOrganizationLayout />}>
              <Route
                path={`/${WorkflowsRoutes.root}/*`}
                element={wrapInSuspense(<WorkflowsChildRouter />)}
              />
              <Route
                path={`/${SettingsRoutes.root}/${InboxesRoutes.root}/:id/${InboxesRoutes.settings}/${InboxesRoutes.calling}/${InboxesRoutes.smartCallFlow}/edit`}
                element={wrapInSuspense(<SmartCallFlowEdit />)}
              />
            </Route>
          </Route>
          <Route path={`/${SuspendedRoutes.root}/*`} element={<SuspendedRouter />} />
        </Route>

        <Route element={<AuthLayoutVue />}>
          <Route element={<SubscriptionAccessLayout />}>
            <Route element={<AccessOrganizationLayout />}>
              <Route element={<MenuAIAutomationLayout />}>
                <Route path={`/${ChatbotRoutes.root}/*`} element={<ChatbotRouter />} />
                <Route path={`/${CampaignRoutes.root}/*`} element={<CampaignRouter />} />
                <Route path={`/${WorkflowsRoutes.root}`} element={<WorkflowsRouter />} />
                <Route
                  path={`/${HubspotWorkflowsRoutes.root}/*`}
                  element={<HubspotWorkflowsRouter />}
                />
                <Route path={`/${KeywordsRoutes.root}/*`} element={<KeywordsRouter />} />
                <Route path={`/${ClickToTextRoutes.root}/*`} element={<ClickToTextRouter />} />
                <Route path={`/${KnowledgeBaseRoutes.root}/*`} element={<KnowledgeBaseRouter />} />
              </Route>
              <Route element={<MenuOutboundLayout />}>
                <Route path={`/${BroadcastsRoutes.root}/*`} element={<BroadcastsRouter />} />
                <Route path={`/${TriggersRoutes.root}/*`} element={<TriggersRouter />} />
                <Route path={`/${PowerDialerRoutes.root}/*`} element={<PowerDialerRouter />} />
                <Route path={`/${ReviewsRoutes.root}/*`} element={<ReviewsRouter />} />
                <Route path={`/${SurveysRoutes.root}/*`} element={<SurveysRouter />} />
              </Route>
              <Route element={<MenuAnalyticsLayout />}>
                <Route path={`/${AnalyticsRoutes.root}/*`} element={<AnalyticsRouter />} />
              </Route>
              <Route path={`/${ClientsRoutes.root}/*`} element={<ClientsRouter />} />
              <Route path={`/${ConversationRoutes.root}/*`} element={<ConversationsRouter />} />
              <Route path={`/${DiscoverRoutes.root}/*`} element={<DiscoverRouter />} />
              <Route path={`/${ContactsRoutes.root}/*`} element={<ContactsRouter />} />
              <Route path={`/${AdminRoutes.root}/*`} element={wrapInSuspense(<AdminRouter />)} />
            </Route>

            <Route path={`/${SettingsRoutes.root}/*`} element={<SettingsRouter />} />

            <Route
              path={`/${TextEnableLandlineRoutes.rootOld}/`}
              element={
                <Navigate
                  to={`/${SettingsRoutes.root}/${NumbersRoutes.root}/${TextEnableLandlineRoutes.root}`}
                  replace
                />
              }
            />
          </Route>
        </Route>

        <Route path={`/${LandingMobileRoutes.root}/*`} element={<LandingMobileRouter />} />

        <Route element={<ReCaptchaLayout />}>
          <Route path={`/${AuthRoutes.root}/*`} element={<AuthRouter />} />
          <Route path={`/${RegisterRoutes.root}/*`} element={<RegisterRouter />} />
        </Route>

        <Route element={<PageNotFoundVueLayout />}>
          <Route path='/*' element={<PageNotFound />} />
        </Route>
      </Route>
    </Route>
  </Route>
)

const router = wrapCreateBrowserRouterV7(createBrowserRouter)(routes)

type INextServiceProps = {
  entities: EntitiesStore
  callPopUpGlobalStore: CallPopUpGlobalStore
}

export const NextService = ({ entities, callPopUpGlobalStore }: INextServiceProps) => (
  <ProviderEntities entities={entities}>
    <StatsigAppProvider>
      <ProviderCallPopUpGlobal value={callPopUpGlobalStore}>
        <RouterProvider router={router} />
      </ProviderCallPopUpGlobal>
    </StatsigAppProvider>
  </ProviderEntities>
)
